import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { logError } from '@/lib/sentry';
import { fetchInventoryLedgerPage } from '@/supabase/inventory/service/inventoryService';
import { EMPTY_LEDGER_FILTERS } from '@/supabase/inventory/types/inventory';
import type { InventoryLedgerEntryWithName, LedgerFilters } from '@/supabase/inventory/types/inventory';

const PAGE_SIZE = 50;

export interface UseInventoryLedgerResult {
  entries: InventoryLedgerEntryWithName[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  // seq가 실제로 반영된("applied") 성공에만 true — superseded(더 최신 reload가 먼저/나중에
  // 시작돼 이 호출의 결과가 버려짐)면 상태를 전혀 건드리지 않고 false를 돌려준다(codexloop R2
  // Fix 2). 호출부(Inventory.tsx runLedgerReload)가 hasLoadedLedgerRef를 "진짜 반영된 로드"에만
  // 세우기 위한 신호 — superseded에 true를 주면 실제로는 화면에 아무 것도 안 채워졌는데
  // "최초 로드 완료"로 오기록되어, 다음 재진입에서 재시도가 안 되는 함정이 생긴다.
  reload: () => Promise<boolean>;
  // 가장 최근 reload()가 실패했는지(초기 로드 실패 감지용, codexloop R1 Fix D). 성공 시 클리어.
  // loadMore 실패는 toast만(hasError를 건드리지 않음) — 이미 일부 로드된 상태를 전체 오류
  // 화면으로 되돌리면 회귀다.
  hasError: boolean;
  // PR7: 현재 적용된 서버측 필터(전부 null = 무필터 → 기존과 동일 쿼리). 값은 applyFilters로만
  // 바꾼다 — reload/loadMore가 fetch 시점에 filtersRef를 읽으므로 state만 바꾸는 우회 경로가
  // 생기면 화면 표시와 실제 쿼리가 갈라진다.
  filters: LedgerFilters;
  // 필터 갱신 후 reload() 호출 — 반환값은 reload의 applied-only boolean을 그대로 pass-through
  // (superseded면 false). reload가 이미 latest-wins seq라 로딩 중 필터 연타 경합은 추가 장치
  // 없이 마지막 것만 반영된다.
  applyFilters: (next: LedgerFilters) => Promise<boolean>;
}

/**
 * 재고 원장(불변 로그) 페이지네이션 도메인 훅(PR2 Task2-C, codexloop R1 Fix B로 reload 재작성,
 * codexloop R2로 id-keyset 페이지네이션 + reload applied-only + loadMore/reload 락 분리).
 * mount 시 자동 로드 없음(lazy) — 원장 탭 진입 시 호출부가 reload()를 호출해야 한다.
 *
 * 페이지네이션은 id 커서(keyset) — entriesRef(현재 로드된 목록)의 마지막 원소 id를 beforeId로
 * 넘긴다. entries state를 콜백 deps에 넣으면 매 페이지 로드마다 loadMore/reload 참조가 바뀌어
 * (memo 자식 stale-prop 위험) 안 되므로 ref로 미러링. hasMore = 마지막 페이지 길이 ===
 * PAGE_SIZE. reload는 초기화 후 첫 페이지(beforeId 없음), loadMore는 커서 이전 페이지를 append.
 * 실패 시 toast + 기존 entries 유지(throw 금지).
 *
 * id-dedupe 제거(codexloop R1에서 추가했던 것을 R2에서 제거): offset 페이지네이션 시절엔 상단
 * 삽입으로 페이지 경계가 밀려 같은 행이 두 번 조회될 수 있어 dedupe가 필요했다. id keyset은
 * beforeId 미만만 조회하므로 이미 로드한 행(id >= beforeId)이 다음 페이지에 다시 나타나는 게
 * 구조적으로 불가능 — dedupe는 이제 죽은 방어라 제거한다(불필요한 O(n) Set 생성 제거 겸함).
 *
 * reload는 isFetchingRef skip-가드가 아니라 latest-wins seq 토큰(reloadSeqRef, useInventory.refresh의
 * refreshSeqRef와 동일 패턴)을 쓴다 — 첫 로드 중 뮤테이션이 새 reload를 쏘는 상황(원장 탭 스켈레톤
 * 로딩 중 신규 상품 등록 등)에서 skip 가드면 두 번째 호출이 조용히 드롭돼 최신 결과가 반영 안 될
 * 수 있다. 매 reload 호출은 항상 fetch하되, entries/hasMore/hasError/isLoading 반영은 "그 호출이
 * 여전히 최신(seq === reloadSeqRef.current)"일 때만 한다. boolean 반환값도 동일 기준(seq 일치)을
 * 쓴다 — superseded면 성공/실패 여부와 무관하게 false(codexloop R2 Fix 2, 이전엔 fetch 성패만
 * 보고 superseded여도 true를 돌려줘 "반영 안 됐는데 성공"으로 잘못 보고하는 문제가 있었다).
 *
 * loadMore/reload는 각자 전용 락(isLoadingMoreRef/isReloadingRef)만 set/clear한다(codexloop R2
 * Fix 3) — 이전엔 공유 isFetchingRef를 loadMore의 finally가 무조건 clear해, superseded된
 * loadMore가 끝나면서 여전히 진행 중인 reload의 락까지 풀어버리는 문제가 있었다(더 보기가
 * 재활성화돼 stale 커서로 또 fetch될 수 있음). reload는 시작 가드가 없다(seq 토큰이 겹침을
 * 처리). loadMore는 자신의 락 또는 reload 락이 걸려 있으면 시작하지 않는다(reload가 entries를
 * 통째로 교체하는 도중엔 커서 기준 append를 시작하지 않기 위함). 노출되는 isLoading은 두 락의
 * OR — 각 경로의 finally에서 전환 후 다시 계산해 LedgerList 기존 props 계약(entries 0건이면
 * 스켈레톤, 있으면 '더 보기' disabled)을 그대로 유지한다.
 *
 * PR7 서버측 필터: filtersRef를 fetch 인자에 얹는 것뿐 — 페이지네이션/seq/락 기계와 직교한다.
 * loadMore 진행 중 필터가 바뀌면 applyFilters의 reload가 seq를 올리므로, 옛 필터 조건으로
 * 조회된 그 loadMore 결과는 기존 인터리브 가드(reloadSeqRef !== seqAtStart)가 자동으로 버린다
 * (옛 필터 페이지가 새 필터 목록에 섞여 append되는 오염이 구조적으로 불가능).
 */
export function useInventoryLedger(): UseInventoryLedgerResult {
  const [entries, setEntries] = useState<InventoryLedgerEntryWithName[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  // 필터는 state(UI 표시용)와 ref(fetch가 읽는 원본)를 쌍으로 미러 — filters state를 reload/
  // loadMore deps에 넣으면 필터 변경마다 두 콜백 참조가 바뀌므로(entriesRef와 동일한 이유)
  // ref로 미러해 fetch 시점에 최신 값을 읽는다.
  const [filters, setFilters] = useState<LedgerFilters>(EMPTY_LEDGER_FILTERS);
  const filtersRef = useRef<LedgerFilters>(EMPTY_LEDGER_FILTERS);
  const entriesRef = useRef<InventoryLedgerEntryWithName[]>([]);
  const isReloadingRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const reloadSeqRef = useRef(0);

  const syncIsLoading = useCallback(() => {
    setIsLoading(isReloadingRef.current || isLoadingMoreRef.current);
  }, []);

  const reload = useCallback(async (): Promise<boolean> => {
    const seq = ++reloadSeqRef.current;
    isReloadingRef.current = true;
    syncIsLoading();
    try {
      const page = await fetchInventoryLedgerPage({ limit: PAGE_SIZE, filters: filtersRef.current });
      if (seq !== reloadSeqRef.current) return false;
      entriesRef.current = page;
      setEntries(page);
      setHasMore(page.length === PAGE_SIZE);
      setHasError(false);
      return true;
    } catch (err) {
      logError('useInventoryLedger.reload', err);
      if (seq === reloadSeqRef.current) {
        toast.error('입출고 내역을 불러오지 못했습니다');
        setHasError(true);
      }
      return false;
    } finally {
      // stale(superseded) reload의 finally가 최신 reload의 락/로딩을 끄면 안 된다
      // (codexloop R3 P2 — 스켈레톤 조기 해제로 빈 상태 오표시·더 보기 재활성 가능).
      // 자기 seq가 여전히 최신일 때만 해제 — 최신 reload는 자기 finally에서 정리한다.
      if (seq === reloadSeqRef.current) {
        isReloadingRef.current = false;
        syncIsLoading();
      }
    }
  }, [syncIsLoading]);

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || isReloadingRef.current) return;
    if (entriesRef.current.length === 0) return;
    isLoadingMoreRef.current = true;
    syncIsLoading();
    // reload와의 인터리브 가드용 스냅샷 — fetch가 끝났을 때 이 값이 바뀌어 있으면 그 사이 reload가
    // entries를 통째로 교체했다는 뜻이라 이 결과(append)는 버린다.
    const seqAtStart = reloadSeqRef.current;
    try {
      const beforeId = entriesRef.current[entriesRef.current.length - 1].id;
      const page = await fetchInventoryLedgerPage({ beforeId, limit: PAGE_SIZE, filters: filtersRef.current });
      if (reloadSeqRef.current !== seqAtStart) return;
      // dedupe 불필요(위 훅 docstring 참고) — id < beforeId만 조회하므로 경계 중복이 구조적으로
      // 없다. id desc 정렬이 그대로 유지되며 append.
      entriesRef.current = [...entriesRef.current, ...page];
      setEntries(entriesRef.current);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      logError('useInventoryLedger.loadMore', err);
      toast.error('입출고 내역을 불러오지 못했습니다');
    } finally {
      isLoadingMoreRef.current = false;
      syncIsLoading();
    }
  }, [syncIsLoading]);

  // ref 갱신이 setFilters보다 먼저인 이유: reload의 fetch는 filtersRef만 읽으므로 ref를 먼저
  // 확정해야 이어지는 reload가 반드시 새 필터로 조회한다(setState는 비동기라 순서 보장이 없음).
  const applyFilters = useCallback(async (next: LedgerFilters): Promise<boolean> => {
    filtersRef.current = next;
    setFilters(next);
    // 필터가 바뀌는 순간 기존 entries는 "다른 질의의 결과"가 된다 — 남겨두면 reload 실패 시
    // (entries 비어있지 않으면 오류 화면을 안 띄우는 LedgerList 계약과 결합해) 옛 필터 목록이
    // 새 필터 라벨 아래 그대로 남고, '더 보기'가 옛 커서+새 필터 혼합 쿼리로 append 오염까지
    // 가능하다(codexloop PR7 R1 P1). 선제 초기화로 실패 경로를 기존 "오류+다시 시도" 화면으로
    // 수렴시키고, entriesRef가 비므로 loadMore도 기존 length===0 가드가 구조적으로 차단한다.
    // 뮤테이션 후 재조회(reload 직접 호출) 경로는 필터가 안 바뀌므로 이 초기화가 불필요하고,
    // 실패 시 기존 목록 유지가 여전히 올바른 동작이라 여기(applyFilters)에만 둔다.
    entriesRef.current = [];
    setEntries([]);
    setHasMore(false);
    return reload();
  }, [reload]);

  return { entries, isLoading, hasMore, loadMore, reload, hasError, filters, applyFilters };
}
